import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  cn,
} from '@design-system';
import { PageLink } from '@/components/PageLink';
import { EditIconButton } from '@/components/EditIconButton';
import CareEnvironmentCostModal from '@/components/CareEnvironmentCostModal';
import EditHealthCareInflationModal from '@/components/EditHealthCareInflationModal';
import {
  OnboardingLayout,
  useSlideSetup,
  useCarePhasesSummarySlide,
  CustomizePlanButtonGroup,
  type CarePhasesSummaryData,
} from '@/features/client-onboarding';
import { OnboardingFundingSources } from '@/pages/OnboardingFundingSources';
import { OnboardingCareSettingsPhaseThree } from '@/pages/OnboardingCareSettings';
import { GenworthPolicyIntro } from '@/features/genworth/education/policy-education-slides';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import { PhaseInflationScheduleTooltip } from '@client/components/PhaseInflationScheduleTooltip';
import { useSelector } from 'react-redux';
import { selectSession, selectFeatureFlags } from '@/model';
import { useNavigate } from 'react-router-dom';
import { SessionType } from '@shared';

import {
  HospitalIcon,
  HouseIcon,
  HouseHeartIcon,
  XIcon,
  EqualIcon,
  ChevronRightIcon,
} from 'lucide-react';

import {
  CareEnvironment,
  formatCurrency,
  formatThousands,
  formatPercent,
  isHealthcareInflationRateDefault,
  appliedHealthCareInflation,
} from '@shared';

export function OnboardingCarePhasesSummary({ clientId }: ClientIdProps) {
  const {
    client,
    allPhaseInflatedProfessionalShareCost,
    allPhaseInflatedRoomAndBoardCost,
    healthCareInflationRate,
    useMonthBasedGrowth,
    visibleCarePhaseData,
    isFullCareHome,
    openEditCostModal,
    setOpenEditCostModal,
    openEditInflationModal,
    setOpenEditInflationModal,
    selectedCareEnvironment,
    handleEditCostClick,
    handleEditInflationClick,
  } = useCarePhasesSummarySlide();

  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;
  const isGenworthMode = !!featureFlags.genworthMode;
  const navigate = useNavigate();

  const appliedInflationRate = appliedHealthCareInflation(client);

  const multSign = <XIcon className="text-muted-foreground size-4 shrink-0" />;
  const equalSign = <EqualIcon className="text-muted-foreground size-4 shrink-0" />;

  // Inflation-factor box shared by the two Independent Living equations (care +
  // room & board), so both rows read the same growth factor and label.
  const inflationFactorBox = (phase: CarePhasesSummaryData) => (
    <div className="bg-muted flex w-full flex-row-reverse items-center justify-between rounded-lg px-2 py-1 sm:flex-col sm:items-start">
      <div className="flex items-center gap-px">
        <p className="text-base text-gray-950">{formatPercent(phase.phaseInflationFactor, 1)}</p>
        <EditIconButton onClick={handleEditInflationClick} tooltipText="Edit inflation rate" />
      </div>
      <p className="text-foreground flex items-center gap-1 pl-1 text-sm font-medium sm:pl-0">
        {useMonthBasedGrowth && phase.phaseInflationFactor ? (
          <>
            {`Avg. cost growth${isHealthcareInflationRateDefault(healthCareInflationRate) ? '' : ' (custom rate)'}`}
            <PhaseInflationScheduleTooltip
              inflationRate={appliedInflationRate}
              schedule={phase.inflationSchedule}
              phaseInflationFactor={phase.phaseInflationFactor}
            />
          </>
        ) : (
          `Cost growth over ${phase.yearsTillPhaseEnd ? formatThousands(phase.yearsTillPhaseEnd) : '0'} years${isHealthcareInflationRateDefault(healthCareInflationRate) ? '' : ' (custom rate)'}`
        )}
      </p>
    </div>
  );

  const iconPicker = (careEnvironment: CareEnvironment) => {
    switch (careEnvironment) {
      case CareEnvironment.home:
        return <HouseIcon className="size-4" />;
      case CareEnvironment.fullCareFacility:
        return <HospitalIcon className="size-4" />;
      case CareEnvironment.assistedLiving:
        return <HouseHeartIcon className="size-4" />;
      case CareEnvironment.independentLiving:
        return <HouseIcon className="size-4" />;
    }
  };

  const handleD2CNext = () => {
    navigate(`/clients/${clientId}/onboarding/guided-planning`);
  };

  useSlideSetup({
    slidePage: OnboardingCarePhasesSummary,
    nextButtonLabel: isD2C
      ? 'Next'
      : isGenworthMode
        ? 'Explore My Policy Benefits'
        : 'Begin Funding Plan',
    previousSlidePage: isFullCareHome ? undefined : OnboardingCareSettingsPhaseThree,
    onNextClick: isD2C ? handleD2CNext : undefined,
  });

  return (
    <OnboardingLayout>
      <div className="flex flex-col justify-center gap-6 py-6 md:py-16">
        <div className="flex w-full flex-wrap items-start justify-between gap-x-6 gap-y-4">
          <h1
            data-testid="care-summary-header"
            className="text-balance text-2xl font-bold text-gray-950 md:text-4xl"
          >
            Your LTC Needs
          </h1>
          <CustomizePlanButtonGroup client={client} />
        </div>
        <div>
          <Accordion type="multiple">
            {visibleCarePhaseData.map((phase, idx) => (
              <AccordionItem
                key={phase.id}
                value={String(phase.id)}
                className={cn(
                  'data-[state=open]:bg-background mb-0 items-center rounded-none border-x border-b-0 border-t p-0',
                  {
                    'rounded-t-xl': idx === 0,
                  },
                )}
              >
                <AccordionTrigger
                  data-testid={`care-summary-phase-${phase.phaseLabel.replace(/\s+/g, '-').toLowerCase()}-button`}
                  className="gap-0"
                >
                  <div className="flex w-full items-center justify-between pr-4 text-base font-medium md:text-xl">
                    <div className="flex items-center gap-2 sm:gap-4">
                      <div className="text-start text-gray-950">{phase.phaseLabel}</div>
                      <Badge variant="secondary">
                        {iconPicker(phase.careEnvironment)}
                        <span>{phase.careEnvironmentLabel}</span>
                      </Badge>
                    </div>
                    <div className="text-gray-950">
                      <LtcCostDisplay value={phase.phaseInflatedTotalCost} />
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="p-0">
                  {phase.hasRoomAndBoard ? (
                    <div className="border-border flex flex-col gap-4 border-t p-4">
                      {/* Independent Living splits into home-style care
                          (policy-eligible) + out-of-pocket room & board. Each is
                          priced as rate × quantity × inflation so the equation
                          multiplies out, mirroring the other care environments. */}
                      <div className="flex flex-col gap-2">
                        <p className="text-sm font-semibold text-gray-950">Care Services</p>
                        <div className="flex flex-col items-stretch gap-2 sm:flex-row">
                          <div className="bg-muted flex w-full flex-row-reverse items-center justify-between rounded-lg px-2 py-1 sm:flex-col sm:items-start">
                            <p className="text-base text-gray-950">{`${formatCurrency(phase.homeRateAmount)}/hr`}</p>
                            <p className="text-foreground pl-1 text-sm font-medium sm:pl-0">
                              Hourly care rate
                            </p>
                          </div>
                          <div className="grid place-items-center">{multSign}</div>
                          <div className="bg-muted flex w-full flex-row-reverse items-center justify-between rounded-lg px-2 py-2 sm:flex-col sm:items-start sm:py-1">
                            <p className="pr-1 text-base text-gray-950 sm:pl-0">
                              {`${formatThousands(phase.phaseProfessionalCareHoursProvided)} hours`}
                            </p>
                            <p className="text-foreground pl-1 text-sm font-medium sm:pl-0">
                              Professional care hours
                            </p>
                          </div>
                          <div className="grid place-items-center">{multSign}</div>
                          {inflationFactorBox(phase)}
                          <div className="grid place-items-center">{equalSign}</div>
                          <div className="bg-muted flex w-full flex-row-reverse items-center justify-between rounded-lg px-2 py-2 sm:w-max sm:flex-col sm:items-start sm:py-1">
                            <p className="pr-1 text-base text-gray-950 sm:pl-0">
                              <LtcCostDisplay value={phase.phaseInflatedProfessionalShareCost} />
                            </p>
                            <p className="text-foreground pl-1 text-sm font-medium sm:pl-0">Total</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <p className="text-sm font-semibold text-gray-950">Room &amp; Board</p>
                        <div className="flex flex-col items-stretch gap-2 sm:flex-row">
                          <div className="bg-muted flex w-full flex-row-reverse items-center justify-between rounded-lg px-2 py-1 sm:flex-col sm:items-start">
                            <div className="flex items-center gap-px">
                              <p className="text-base text-gray-950">{`${formatCurrency(phase.rateAmount)}/mo`}</p>
                              <EditIconButton
                                onClick={() => handleEditCostClick(phase.careEnvironment)}
                                tooltipText="Edit room & board cost"
                              />
                            </div>
                            <p className="text-foreground pl-1 text-sm font-medium sm:pl-0">
                              Monthly rate
                            </p>
                          </div>
                          <div className="grid place-items-center">{multSign}</div>
                          <div className="bg-muted flex w-full flex-row-reverse items-center justify-between rounded-lg px-2 py-2 sm:flex-col sm:items-start sm:py-1">
                            <p className="pr-1 text-base text-gray-950 sm:pl-0">
                              {`${formatThousands(phase.phaseCareMonthsNeeded)} months`}
                            </p>
                            <p className="text-foreground pl-1 text-sm font-medium sm:pl-0">
                              {phase.isDurationCustom ? 'Custom duration' : 'Estimated duration'}
                            </p>
                          </div>
                          <div className="grid place-items-center">{multSign}</div>
                          {inflationFactorBox(phase)}
                          <div className="grid place-items-center">{equalSign}</div>
                          <div className="bg-muted flex w-full flex-row-reverse items-center justify-between rounded-lg px-2 py-2 sm:w-max sm:flex-col sm:items-start sm:py-1">
                            <p className="pr-1 text-base text-gray-950 sm:pl-0">
                              <LtcCostDisplay value={phase.phaseInflatedRoomAndBoardCost} />
                            </p>
                            <p className="text-foreground pl-1 text-sm font-medium sm:pl-0">Total</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between px-3 pt-1">
                        <p className="text-base font-medium text-gray-950">Total</p>
                        <p className="text-base font-medium text-gray-950">
                          <LtcCostDisplay value={phase.phaseInflatedTotalCost} />
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="border-border flex flex-col items-stretch gap-2 border-t p-4 sm:flex-row">
                    <div className="bg-muted flex w-full flex-row-reverse items-center justify-between rounded-lg px-2 py-1 sm:flex-col sm:items-start">
                      <div className="flex items-center gap-px">
                        <p className="text-base text-gray-950">
                          {`${formatCurrency(phase.rateAmount)}/${phase.inHomeEnvironment ? 'hr' : 'mo'}`}
                        </p>
                        <EditIconButton
                          onClick={() => handleEditCostClick(phase.careEnvironment)}
                          tooltipText="Edit cost"
                        />
                      </div>
                      <p className="text-foreground pl-1 text-sm font-medium sm:pl-0">
                        {phase.rateLocaleDescription}
                      </p>
                    </div>
                    <div className="grid place-items-center">{multSign}</div>
                    <div className="bg-muted flex w-full flex-row-reverse items-center justify-between rounded-lg px-2 py-2 sm:flex-col sm:items-start sm:py-1">
                      <p className="pr-1 text-base text-gray-950 sm:pl-0">
                        {phase.inHomeEnvironment
                          ? `${formatThousands(phase.phaseProfessionalCareHoursProvided)} hours`
                          : `${formatThousands(phase.phaseCareMonthsNeeded)} months`}
                      </p>
                      <p className="text-foreground pl-1 text-sm font-medium sm:pl-0">
                        {phase.inHomeEnvironment
                          ? `Professional care hours`
                          : `${phase.isDurationCustom ? 'Custom duration' : 'Estimated duration'}`}
                      </p>
                    </div>
                    <div className="grid place-items-center">{multSign}</div>
                    <div className="bg-muted flex w-full flex-row-reverse items-center justify-between rounded-lg px-2 py-1 sm:flex-col sm:items-start">
                      <div className="flex items-center gap-px">
                        <p className="text-base text-gray-950">
                          {formatPercent(phase.phaseInflationFactor, 1)}
                        </p>
                        <EditIconButton
                          onClick={handleEditInflationClick}
                          tooltipText="Edit inflation rate"
                        />
                      </div>
                      <p className="text-foreground flex items-center gap-1 pl-1 text-sm font-medium sm:pl-0">
                        {useMonthBasedGrowth && phase.phaseInflationFactor ? (
                          <>
                            {`Avg. cost growth${isHealthcareInflationRateDefault(healthCareInflationRate) ? '' : ' (custom rate)'}`}
                            <PhaseInflationScheduleTooltip
                              inflationRate={appliedInflationRate}
                              schedule={phase.inflationSchedule}
                              phaseInflationFactor={phase.phaseInflationFactor}
                            />
                          </>
                        ) : (
                          `Cost growth over ${phase.yearsTillPhaseEnd ? formatThousands(phase.yearsTillPhaseEnd) : '0'} years${isHealthcareInflationRateDefault(healthCareInflationRate) ? '' : ' (custom rate)'}`
                        )}
                      </p>
                    </div>
                    <div className="grid place-items-center">{equalSign}</div>
                    <div className="bg-muted flex w-full flex-row-reverse items-center justify-between rounded-lg px-2 py-2 sm:w-max sm:flex-col sm:items-start sm:py-1">
                      <p className="pr-1 text-base text-gray-950 sm:pl-0">
                        <LtcCostDisplay value={phase.phaseInflatedProfessionalShareCost} />
                      </p>
                      <p className="text-foreground pl-1 text-sm font-medium sm:pl-0">Total</p>
                    </div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="bg-muted pr-13 flex justify-between rounded-b-xl border py-4 pl-4">
            <div className="text-base text-gray-950 md:text-2xl">Total Care Needs</div>
            <div
              className="text-base text-gray-950 md:text-2xl"
              data-testid="care-summary-total-amount"
            >
              <LtcCostDisplay
                value={allPhaseInflatedProfessionalShareCost + allPhaseInflatedRoomAndBoardCost}
              />
            </div>
          </div>
          {isD2C ? (
            <button
              className="mt-6 hidden cursor-pointer gap-2 text-lg font-light hover:underline md:flex md:text-xl"
              onClick={handleD2CNext}
            >
              <div className="flex items-center gap-1 font-normal">
                <ChevronRightIcon className="size-6" />
                Next step:
              </div>
              <span> Build your step-by-step funding plan</span>
            </button>
          ) : (
            <PageLink
              className="mt-6 hidden gap-2 text-lg font-light hover:underline md:flex md:text-xl"
              to={isGenworthMode ? GenworthPolicyIntro : OnboardingFundingSources}
              targetProps={{ clientId }}
            >
              <div className="flex items-center gap-1 font-normal">
                <ChevronRightIcon className="size-6" />
                Next step:
              </div>
              <span> Explore the best ways to protect yourself in old age</span>
            </PageLink>
          )}
          {selectedCareEnvironment && (
            <CareEnvironmentCostModal
              selectedCareEnvironment={selectedCareEnvironment}
              open={openEditCostModal}
              setOpen={setOpenEditCostModal}
            />
          )}
          <EditHealthCareInflationModal
            open={openEditInflationModal}
            setOpen={setOpenEditInflationModal}
          />
        </div>
      </div>
    </OnboardingLayout>
  );
}
